import { planPriceCents } from "@amtana/billing-contract";
import type { PaidPlan, PlanId, SubscriptionStatus } from "@amtana/billing-contract";
import type { Catalogs, Formats, Translation, Translate } from "@amtana/i18n/catalog";

// The shell's words: plan names and the banner each account status shows
// (S6.05): one key per string, in English and Spanish. `statusBanner`/`emailVerificationBanner`
// stay pure (model/ rule): the caller passes in the already-resolved
// translate function (`useShellCopy`, behavior/use-shell-copy.ts) and the
// date, formatted in the active language by the caller too.

export type ShellCopyKey = keyof typeof EN;
export type ShellCatalog = typeof EN;

const EN = {
  planFree: "Free plan",
  planStarter: "Starter",
  planGrowth: "Growth",
  planEnterprise: "Enterprise",
  planUnlimited: "Unlimited",
  noSubscriptionTitle: "No Active Subscription",
  noSubscriptionDescription: "You don't have any active subscription.",
  activate: "Activate",
  pastDueTitle: "Payment Issue Detected",
  pastDueDescription:
    "There was a problem processing your payment. Review your payment method to avoid losing access.",
  review: "Review",
  frozenTitle: "Account Frozen",
  frozenDescription: "Your account is currently frozen. Access will be restored on {date}.",
  cancelScheduledTitle: "Subscription Scheduled for Cancellation",
  cancelScheduledDescription:
    "Your subscription will be cancelled on {date}. All your data will be permanently deleted immediately after.",
  resubscribe: "Resubscribe",
  disputeTitle: "Account restricted: active dispute",
  disputeDescription:
    "Your account has been restricted due to an open payment dispute. Withdraw it with your payment provider to restore access.",
  emailUnverifiedTitle: "Email Verification Pending",
  emailUnverifiedDescription: "Verify your email to continue using {product}.",
  verifyEmailTitle: "Verify your email address",
  verifyEmailDescription: "Confirm the link we mailed you to keep access to your account.",
  statusActive: "Active subscription",
  statusNoSubscription: "No subscription",
  statusPastDue: "Payment past due",
  statusFrozen: "Frozen",
  statusCancelScheduled: "Cancellation scheduled",
  statusDispute: "Payment dispute",
  statusEmailUnverified: "Email not verified",
  soon: "soon",
  upgrade: "Upgrade",
  accountMenu: "Account menu",
  account: "Account",
  myAccount: "My Account",
  settings: "Settings",
  buyCredits: "Buy credits",
  buyAiCredits: "Buy AI credits",
  inviteMembers: "Invite Members",
  simulation: "Simulation…",
  helpCenter: "Help Center",
  helpCenterToast: "Opens the help center in the real product.",
  logOut: "Log Out",
  simulationDialogTitle: "Simulation",
  simulationDialogDescription:
    "Put the account on any plan and status to see every gated and alert state. Nothing is charged.",
  cancel: "Cancel",
  apply: "Apply",
  plan: "Plan",
  accountStatus: "Account status",
  snoozeForADay: "Snooze for a day",
  resendLink: "Resend link",
  notFoundTitle: "This page does not exist",
  notFoundDescription: "It may not be built yet in the replica, or the link is out of date.",
  backToDashboard: "Back to the dashboard",
  impersonationBannerTitle: "Acting as a customer",
  impersonationBannerDescription:
    "{actorName} is using the platform as {targetEmail}. Every change is recorded.",
  stopViewing: "Stop viewing",
  viewingAs: "Viewing as {targetEmail}",
  readOnly: "every change recorded",
  stop: "Stop",
  planWithPrice: "{plan} · €{price}/mo",
  groupStartSomething: "Start something",
  groupGoTo: "Go to",
  groupLeads: "Leads",
  groupOutreach: "Outreach",
  groupYou: "You",
  groupWorkspace: "Workspace",
  groupEnterprise: "Enterprise",
  groupBilling: "Billing",
  groupRewards: "Rewards",
  groupTenants: "Tenants",
  groupPlatform: "Platform",
  groupOperations: "Operations",
  sectionBackOffice: "Back office",
  shortcutNewAnalysis: "Find leads in an account's followers",
  shortcutNewAnalysisKeywords: "new analysis scrape instagram tiktok",
  shortcutNewCampaign: "Write a new email campaign",
  shortcutNewCampaignKeywords: "new campaign sequence outreach",
  shortcutFindAccounts: "Search accounts by niche",
  shortcutFindAccountsKeywords: "finder influencer niche accounts",
  shortcutBuyMailboxes: "Buy domains and mailboxes",
  shortcutBuyMailboxesKeywords: "managed mailbox domain sender",
  shortcutMyAccount: "My account",
  shortcutMyAccountKeywords: "profile preferences language",
  shortcutSecurity: "Security",
  shortcutSecurityKeywords: "password passkey sessions sign-in google linkedin",
  shortcutSubscription: "Subscription",
  shortcutSubscriptionKeywords: "billing plan invoices seats",
  shortcutUsage: "Usage",
  shortcutUsageKeywords: "credits limits",
  shortcutSettingsKeywords: "preferences",
  shortcutSso: "Single sign-on",
  shortcutSsoKeywords: "sso okta saml oidc domain security",
  shortcutPricing: "Plans & pricing",
  shortcutPricingKeywords: "upgrade plan",
  supportMenu: "Support",
  supportHelpCenter: "Help center",
  supportStatusPage: "Status page",
  supportFeatureRequest: "Feature request",
  supportReportBug: "Report a bug",
  supportOutsideHint: "Opens outside the app in the real product.",
  searchPlaceholder: "Search pages or start something…",
  loadingAccount: "Loading your account",
  backToProduct: "Back to {product}",
  simulationUpdated: "Simulation updated",
  simulationUpdatedHint: "Plan {plan}, status {status}.",
  errorSimulation: "Could not update the simulation",
  footerRights: "© 2026 {product}. All rights reserved.",
  errorSignOut: "Could not sign out",
  creditsLabel: "Credits",
  creditsLeft: "{left} left",
  creditsUnlimited: "Unlimited",
  emailSent: "Email sent",
  errorEmailSend: "Could not send that email",
  errorStopViewing: "Could not stop viewing as this person",
  notFoundPageTitle: "Not found · {product}",
} as const;

const ES: Translation<ShellCatalog> = {
  planFree: "Plan gratis",
  planStarter: "Starter",
  planGrowth: "Growth",
  planEnterprise: "Enterprise",
  planUnlimited: "Unlimited",
  noSubscriptionTitle: "Sin suscripción activa",
  noSubscriptionDescription: "No tienes ninguna suscripción activa.",
  activate: "Activar",
  pastDueTitle: "Problema con el pago",
  pastDueDescription:
    "Hubo un problema al procesar tu pago. Revisa tu forma de pago para no perder el acceso.",
  review: "Revisar",
  frozenTitle: "Cuenta congelada",
  frozenDescription: "Tu cuenta está congelada. El acceso se restablecerá el {date}.",
  cancelScheduledTitle: "Suscripción programada para cancelarse",
  cancelScheduledDescription:
    "Tu suscripción se cancelará el {date}. Todos tus datos se eliminarán de forma permanente justo después.",
  resubscribe: "Volver a suscribirme",
  disputeTitle: "Cuenta restringida: disputa activa",
  disputeDescription:
    "Tu cuenta está restringida por una disputa de pago abierta. Retírala con tu proveedor de pago para restablecer el acceso.",
  emailUnverifiedTitle: "Verificación de correo pendiente",
  emailUnverifiedDescription: "Verifica tu correo para seguir usando {product}.",
  verifyEmailTitle: "Verifica tu dirección de correo",
  verifyEmailDescription: "Confirma el enlace que te enviamos para no perder el acceso a tu cuenta.",
  statusActive: "Suscripción activa",
  statusNoSubscription: "Sin suscripción",
  statusPastDue: "Pago vencido",
  statusFrozen: "Congelada",
  statusCancelScheduled: "Cancelación programada",
  statusDispute: "Disputa de pago",
  statusEmailUnverified: "Correo no verificado",
  soon: "pronto",
  upgrade: "Mejorar plan",
  accountMenu: "Menú de la cuenta",
  account: "Cuenta",
  myAccount: "Mi cuenta",
  settings: "Configuración",
  buyCredits: "Comprar créditos",
  buyAiCredits: "Comprar créditos de IA",
  inviteMembers: "Invitar miembros",
  simulation: "Simulación…",
  helpCenter: "Centro de ayuda",
  helpCenterToast: "Abre el centro de ayuda en el producto real.",
  logOut: "Cerrar sesión",
  simulationDialogTitle: "Simulación",
  simulationDialogDescription:
    "Pon la cuenta en cualquier plan y estado para ver cada estado bloqueado o de alerta. No se cobra nada.",
  cancel: "Cancelar",
  apply: "Aplicar",
  plan: "Plan",
  accountStatus: "Estado de la cuenta",
  snoozeForADay: "Posponer por un día",
  resendLink: "Reenviar enlace",
  notFoundTitle: "Esta página no existe",
  notFoundDescription: "Puede que todavía no esté construida en la réplica, o el enlace está desactualizado.",
  backToDashboard: "Volver al panel",
  impersonationBannerTitle: "Actuando como cliente",
  impersonationBannerDescription:
    "{actorName} está usando la plataforma como {targetEmail}. Cada cambio queda registrado.",
  stopViewing: "Dejar de ver",
  viewingAs: "Viendo como {targetEmail}",
  readOnly: "cada cambio queda registrado",
  stop: "Salir",
  planWithPrice: "{plan} · €{price}/mes",
  groupStartSomething: "Empezar algo",
  groupGoTo: "Ir a",
  groupLeads: "Leads",
  groupOutreach: "Alcance",
  groupYou: "Tú",
  groupWorkspace: "Espacio de trabajo",
  groupEnterprise: "Enterprise",
  groupBilling: "Facturación",
  groupRewards: "Recompensas",
  groupTenants: "Organizaciones",
  groupPlatform: "Plataforma",
  groupOperations: "Operaciones",
  sectionBackOffice: "Back office",
  shortcutNewAnalysis: "Encontrar leads entre los seguidores de una cuenta",
  shortcutNewAnalysisKeywords: "nuevo análisis extraer instagram tiktok",
  shortcutNewCampaign: "Escribir una nueva campaña de correo",
  shortcutNewCampaignKeywords: "nueva campaña secuencia alcance",
  shortcutFindAccounts: "Buscar cuentas por nicho",
  shortcutFindAccountsKeywords: "buscador influencer nicho cuentas",
  shortcutBuyMailboxes: "Comprar dominios y buzones",
  shortcutBuyMailboxesKeywords: "gestionados buzón dominio remitente",
  shortcutMyAccount: "Mi cuenta",
  shortcutMyAccountKeywords: "perfil preferencias idioma",
  shortcutSecurity: "Seguridad",
  shortcutSecurityKeywords: "contraseña passkey sesiones inicio google linkedin",
  shortcutSubscription: "Suscripción",
  shortcutSubscriptionKeywords: "facturación plan facturas asientos",
  shortcutUsage: "Uso",
  shortcutUsageKeywords: "créditos límites",
  shortcutSettingsKeywords: "preferencias",
  shortcutSso: "Inicio de sesión único",
  shortcutSsoKeywords: "sso okta saml oidc dominio seguridad",
  shortcutPricing: "Planes y precios",
  shortcutPricingKeywords: "mejorar plan",
  supportMenu: "Soporte",
  supportHelpCenter: "Centro de ayuda",
  supportStatusPage: "Página de estado",
  supportFeatureRequest: "Sugerir una función",
  supportReportBug: "Reportar un error",
  supportOutsideHint: "Se abre fuera de la app, en el producto real.",
  searchPlaceholder: "Busca páginas o empieza algo…",
  loadingAccount: "Cargando tu cuenta",
  backToProduct: "Volver a {product}",
  simulationUpdated: "Simulación actualizada",
  simulationUpdatedHint: "Plan {plan}, estado {status}.",
  errorSimulation: "No pudimos actualizar la simulación",
  footerRights: "© 2026 {product}. Todos los derechos reservados.",
  errorSignOut: "No pudimos cerrar la sesión",
  creditsLabel: "Créditos",
  creditsLeft: "Quedan {left}",
  creditsUnlimited: "Ilimitados",
  emailSent: "Correo enviado",
  errorEmailSend: "No pudimos enviar ese correo",
  errorStopViewing: "No pudimos dejar de ver como esta persona",
  notFoundPageTitle: "No encontrada · {product}",
};

export const SHELL_CATALOGS: Catalogs<ShellCatalog> = { en: EN, es: ES };

/** The account status banner's own date, in the active language's locale; no saved
 * time zone is read here (the banner has none to read), unlike S6.06's sessions panel. */
export function formatStatusDate(iso: string, format: Formats): string {
  return format.date(iso, { day: "numeric", month: "long", year: "numeric" });
}

export type StatusBanner = {
  readonly tone: "warning" | "danger" | "info" | "primary";
  readonly title: string;
  readonly description: string;
  readonly action?: { readonly label: string; readonly href: string };
};

export function statusBanner(
  t: Translate<ShellCatalog>,
  status: SubscriptionStatus,
  formattedDate: string | null,
): StatusBanner | undefined {
  const date = formattedDate ?? t("soon");
  switch (status) {
    case "active":
      return undefined;
    case "no-subscription":
      return {
        tone: "warning",
        title: t("noSubscriptionTitle"),
        description: t("noSubscriptionDescription"),
        action: { label: t("activate"), href: "/pricing" },
      };
    case "past-due":
      return {
        tone: "danger",
        title: t("pastDueTitle"),
        description: t("pastDueDescription"),
        action: { label: t("review"), href: "/subscription" },
      };
    case "frozen":
      return {
        tone: "info",
        title: t("frozenTitle"),
        description: t("frozenDescription", { date }),
      };
    case "cancel-scheduled":
      return {
        tone: "warning",
        title: t("cancelScheduledTitle"),
        description: t("cancelScheduledDescription", { date }),
        action: { label: t("resubscribe"), href: "/subscription" },
      };
    case "dispute":
      return { tone: "danger", title: t("disputeTitle"), description: t("disputeDescription") };
  }
}

/** Shown over the account's own banner while the signed-in person has not confirmed their email. */
export function emailVerificationBanner(
  t: Translate<ShellCatalog>,
  emailVerified: boolean,
): StatusBanner | undefined {
  if (emailVerified) return undefined;
  return { tone: "info", title: t("verifyEmailTitle"), description: t("verifyEmailDescription") };
}

/** Shown the whole length of a visit (S9.06, ADR-005 point 6): read-only, who started it, who
 * it names; takes priority over every other banner, and is never snoozed. */
export function impersonationBanner(
  t: Translate<ShellCatalog>,
  options: { readonly actorName: string; readonly targetEmail: string },
): StatusBanner {
  return {
    tone: "warning",
    title: t("impersonationBannerTitle"),
    description: t("impersonationBannerDescription", options),
  };
}

export function statusOptions(
  t: Translate<ShellCatalog>,
): ReadonlyArray<{ value: SubscriptionStatus; label: string }> {
  return [
    { value: "active", label: t("statusActive") },
    { value: "no-subscription", label: t("statusNoSubscription") },
    { value: "past-due", label: t("statusPastDue") },
    { value: "frozen", label: t("statusFrozen") },
    { value: "cancel-scheduled", label: t("statusCancelScheduled") },
    { value: "dispute", label: t("statusDispute") },
  ];
}

/** A plan's monthly price in whole euros, read from the billing contract so the drawer never
 * names a price the plan no longer has (epic 43). */
const planPrice = (plan: PaidPlan): string => String(planPriceCents(plan, "month") / 100);

const PLAN_PRICE: Readonly<Record<PaidPlan, string>> = {
  starter: planPrice("starter"),
  growth: planPrice("growth"),
  enterprise: planPrice("enterprise"),
  unlimited: planPrice("unlimited"),
};

export function planOptions(t: Translate<ShellCatalog>): ReadonlyArray<{ value: PlanId; label: string }> {
  return [
    { value: "free", label: t("planFree") },
    { value: "starter", label: t("planWithPrice", { plan: t("planStarter"), price: PLAN_PRICE.starter }) },
    { value: "growth", label: t("planWithPrice", { plan: t("planGrowth"), price: PLAN_PRICE.growth }) },
    {
      value: "enterprise",
      label: t("planWithPrice", { plan: t("planEnterprise"), price: PLAN_PRICE.enterprise }),
    },
    {
      value: "unlimited",
      label: t("planWithPrice", { plan: t("planUnlimited"), price: PLAN_PRICE.unlimited }),
    },
  ];
}
